import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { format } from "date-fns";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import type { LogEntry } from "../types";
import {
  PERIOD_TAG_INFO,
  formatInsulinDose,
  formatQuickWithPF,
  getQATotal,
  getQAGrandTotal,
  getTimestampMs,
} from "../types";

/**
 * Reusable diary entry card — renders a single LogEntry in the standard
 * card format (header with time/period/edit, data table with GL/carbs/
 * insulin/ketones, optional notes row).
 *
 * Pass `allEntries` to enable insulin-stacking detection (compares against
 * other entries within 3 hours).
 */
@customElement("entry-card")
export class EntryCard extends LitElement {
  @property({ attribute: false }) entry!: LogEntry;

  /** All entries for the day — used for stacking detection. */
  @property({ attribute: false }) allEntries: LogEntry[] = [];

  /** Hide the edit button (e.g. when viewing monitored user data). */
  @property({ type: Boolean }) hideEdit = false;

  static styles = css`
    :host {
      display: block;
    }

    .entry-card {
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      margin-bottom: 0.75rem;
      overflow: hidden;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0.75rem;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .entry-header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .entry-time {
      font-weight: 600;
      font-size: 1.35rem;
    }

    .entry-period {
      background: var(--color-accent-soft, #faf6ff);
      color: var(--color-accent, #bd64fc);
      padding: 0.125rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 1.05rem;
      font-weight: 500;
    }

    .entry-edit-btn {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
      background: var(--color-accent, #bd64fc);
      color: white;
      border: none;
      border-radius: 0.25rem;
      font-size: 1.05rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s;
    }

    .entry-edit-btn:hover {
      filter: brightness(0.92);
    }

    .entry-edit-btn wa-icon {
      font-size: 0.75rem;
    }

    .entry-body {
      padding: 0;
    }

    .entry-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 1.2rem;
    }

    .entry-table tr {
      border-bottom: 1px solid #f0f0f0;
    }

    .entry-table tr:last-child {
      border-bottom: none;
    }

    .entry-table th {
      text-align: left;
      padding: 0.375rem 0.75rem;
      font-weight: 500;
      color: #6c757d;
      background: #fafafa;
      width: 80px;
      font-size: 1.05rem;
      text-transform: uppercase;
    }

    .entry-table td {
      padding: 0.375rem 0.75rem;
      font-weight: 500;
    }

    .entry-table .value-cell {
      font-size: 1.35rem;
    }

    .entry-note-row td {
      background: #f8f9fa;
      font-style: italic;
      color: #495057;
      font-weight: 400;
    }

    /* GL color coding */
    .gl-hypo {
      color: #dc3545;
      font-weight: 700;
    }
    .gl-low {
      color: #fd7e14;
    }
    .gl-target {
      color: #198754;
    }
    .gl-elevated {
      color: #fd7e14;
    }
    .gl-high {
      color: #dc3545;
    }

    /* Ketone color coding */
    .ket-normal {
      color: #198754;
    }
    .ket-elevated {
      color: #fd7e14;
    }
    .ket-high {
      color: #dc3545;
    }
    .ket-danger {
      color: #fff;
      background: #dc3545;
      padding: 0 0.25rem;
      border-radius: 0.25rem;
    }

    /* Stacking indicator */
    .stacking-warning {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      color: #fd7e14;
      font-size: 0.625rem;
      margin-left: 0.25rem;
    }

    /* Sick day indicator */
    .sick-indicator {
      color: #dc3545;
      font-size: 0.85rem;
    }

    /* Exercise indicator */
    .exercise-indicator {
      color: #0d6efd;
      font-size: 0.85rem;
    }

    /* Entry card variants */
    .entry-card.has-hypo {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-high-ket {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-stacking {
      border-left: 3px solid #fd7e14;
    }
  `;

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getKetClass(value?: number): string {
    if (!value) return "";
    if (value < 0.6) return "ket-normal";
    if (value < 1.5) return "ket-elevated";
    if (value < 3.0) return "ket-high";
    return "ket-danger";
  }

  private _hasStackingConcern(): boolean {
    const entry = this.entry;
    // Use grand total so a P/F dose triggers the same warning as a regular QA.
    const qaTotal = getQAGrandTotal(entry);
    if (!qaTotal) return false;

    const entryTime = getTimestampMs(entry.timestamp);
    const threeHoursAgo = entryTime - 3 * 60 * 60 * 1000;

    for (const prior of this.allEntries) {
      if (prior.id === entry.id) continue;
      const priorTime = getTimestampMs(prior.timestamp);
      if (priorTime >= threeHoursAgo && priorTime < entryTime) {
        const priorQA = getQAGrandTotal(prior);
        if (priorQA) return true;
      }
    }
    return false;
  }

  private _getCardClass(): string {
    const entry = this.entry;
    const classes = ["entry-card"];
    if (entry.bloodGlucose && entry.bloodGlucose < 4) classes.push("has-hypo");
    else if (entry.ketones && entry.ketones >= 1.5)
      classes.push("has-high-ket");
    else if (this._hasStackingConcern()) classes.push("has-stacking");
    return classes.join(" ");
  }

  private _editEntry() {
    Router.go(`/edit/${this.entry.id}`);
  }

  render() {
    const entry = this.entry;
    if (!entry) return html``;

    const qaTotal = getQATotal(entry);
    const hasPF = (entry.pfBase ?? 0) > 0;
    const biTotal =
      entry.biBase !== undefined || entry.biCorrection !== undefined
        ? (entry.biBase || 0) + (entry.biCorrection || 0)
        : undefined;
    const hasStacking = this._hasStackingConcern();
    const quickDisplay = hasPF
      ? formatQuickWithPF(entry.qaBase, entry.qaCorrection, entry.pfBase)
      : formatInsulinDose(entry.qaBase, entry.qaCorrection);
    const showQuickRow = qaTotal !== undefined || hasPF;

    const rows: Array<{ label: string; value: ReturnType<typeof html> }> = [];

    if (entry.bloodGlucose) {
      rows.push({
        label: "GL",
        value: html`<span class="${this._getGlClass(entry.bloodGlucose)}"
          >${entry.bloodGlucose.toFixed(1)} mmol/L</span
        >`,
      });
    }

    if (entry.carbs) {
      rows.push({
        label: "Carbs",
        value: html`${entry.carbs}g`,
      });
    } else if (hasPF) {
      // OZDAFNE no-carb convention (handout p.10): record CPs as "0 + 1".
      rows.push({
        label: "Carbs",
        value: html`<span title="No-carb P/F meal: protein/fat counted as 1 CP"
          >0 + 1</span
        >`,
      });
    }

    if (showQuickRow) {
      rows.push({
        label: "Quick",
        value: html`${quickDisplay}u
        ${hasStacking ? html`<span class="stacking-warning">⚠️ IOB</span>` : ""}`,
      });
    }

    if (biTotal !== undefined) {
      rows.push({
        label: "Long",
        value: html`${formatInsulinDose(entry.biBase, entry.biCorrection)}u`,
      });
    }

    if (entry.ketones !== undefined) {
      rows.push({
        label: "Ketones",
        value: html`<span class="${this._getKetClass(entry.ketones)}"
          >${entry.ketones.toFixed(1)} mmol/L</span
        >`,
      });
    }

    if (entry.sickDay) {
      rows.push({
        label: "Sick",
        value: html`<span class="sick-indicator">🤒 Sick day</span>`,
      });
    }

    if (entry.exercise) {
      rows.push({
        label: "Exercise",
        value: html`<span class="exercise-indicator"
          >🏃 ${entry.exercise.duration}min ${entry.exercise.intensity}</span
        >`,
      });
    }

    return html`
      <div class=${this._getCardClass()}>
        <div class="entry-header">
          <div class="entry-header-left">
            <span class="entry-time"
              >${format(
                new Date(getTimestampMs(entry.timestamp)),
                "HH:mm",
              )}</span
            >
            <span class="entry-period"
              >${PERIOD_TAG_INFO[entry.periodTag].label}</span
            >
          </div>
          ${!this.hideEdit
            ? html`<button class="entry-edit-btn" @click=${() => this._editEntry()}>
                <wa-icon name="pen"></wa-icon>
                Edit
              </button>`
            : ""}
        </div>
        <div class="entry-body">
          ${rows.length > 0
            ? html`
                <table class="entry-table">
                  <tbody>
                    ${rows.map(
                      (row) => html`
                        <tr>
                          <th>${row.label}</th>
                          <td class="value-cell">${row.value}</td>
                        </tr>
                      `,
                    )}
                    ${entry.notes
                      ? html`
                          <tr class="entry-note-row">
                            <th>Note</th>
                            <td>${entry.notes}</td>
                          </tr>
                        `
                      : ""}
                  </tbody>
                </table>
              `
            : html`<div
                style="padding: 0.5rem 0.75rem; color: #6c757d; font-size: 0.8rem;"
              >
                No data recorded
              </div>`}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "entry-card": EntryCard;
  }
}
